import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../config/firebaseConfig";
import { getOrSetCache, invalidateCache } from "../utils/cacheUtil";

const STAFF_ATTENDANCE_CACHE_TTL_MS = 90 * 1000;

const ROLE_ALIASES = {
  classteacher: "class teacher",
  "class teacher": "class teacher",
  class_teacher: "class teacher",
  "class-teacher": "class teacher",
  attendant: "attender",
};

const NON_TEACHING_STAFF_EXCLUDED_ROLES = new Set([
  "student",
  "parent",
  "teacher",
  "class teacher",
]);

function normalizeRoleName(role) {
  const cleaned = String(role || "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!cleaned) return "";
  return ROLE_ALIASES[cleaned] || cleaned;
}

function normalizeRoleArray(roles) {
  if (Array.isArray(roles)) {
    return roles
      .map((role) => normalizeRoleName(role))
      .filter(Boolean);
  }

  if (typeof roles === "string") {
    const normalized = normalizeRoleName(roles);
    return normalized ? [normalized] : [];
  }

  return [];
}

function getUserRollNo(user) {
  return user.rollNo || user.employeeId || user.staffId || user.uid || user.id;
}

function buildSummary(users, presentByUser) {
  const byRole = {};
  let allPresentCount = 0;
  let nonTeachingStaffPresentCount = 0;
  let nonTeachingStaffTotalCount = 0;

  users.forEach((user) => {
    const isPresent = presentByUser[user.id] === true;

    if (isPresent) {
      allPresentCount += 1;
    }

    user.roles.forEach((role) => {
      if (!byRole[role]) {
        byRole[role] = { presentCount: 0, totalCount: 0 };
      }

      byRole[role].totalCount += 1;
      if (isPresent) {
        byRole[role].presentCount += 1;
      }
    });

    const isNonTeachingStaff =
      user.roles.length === 0 ||
      user.roles.some((role) => !NON_TEACHING_STAFF_EXCLUDED_ROLES.has(role));

    if (isNonTeachingStaff) {
      nonTeachingStaffTotalCount += 1;
      if (isPresent) {
        nonTeachingStaffPresentCount += 1;
      }
    }
  });

  return {
    all: {
      presentCount: allPresentCount,
      totalCount: users.length,
    },
    staff: {
      presentCount: nonTeachingStaffPresentCount,
      totalCount: nonTeachingStaffTotalCount,
    },
    byRole,
  };
}

class StaffAttendanceService {
  getTodayDateKey() {
    const now = new Date();
    const localDate = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    return localDate.toISOString().split("T")[0];
  }

  formatDateLabel(dateKey) {
    const date = new Date(`${dateKey}T00:00:00`);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  getPresetRoles(preset, roleOptions) {
    if (!preset || roleOptions.length === 0) {
      return [];
    }

    const normalizedPreset = normalizeRoleName(preset);

    if (normalizedPreset === "staff") {
      return roleOptions.filter(
        (role) => !NON_TEACHING_STAFF_EXCLUDED_ROLES.has(role),
      );
    }

    if (roleOptions.includes(normalizedPreset)) {
      return [normalizedPreset];
    }

    return [];
  }

  getRoleCounts(summary, role) {
    const normalizedRole = normalizeRoleName(role);
    return summary?.byRole?.[normalizedRole] || { presentCount: 0, totalCount: 0 };
  }

  async getAttendanceDataByDate(dateKey, options = {}) {
    const { forceRefresh = false } = options;
    const cacheKey = `staff:attendance:date:${dateKey}`;

    if (forceRefresh) {
      invalidateCache(cacheKey);
    }

    return getOrSetCache(
      cacheKey,
      async () => {
        const [usersSnapshot, attendanceSnapshot] = await Promise.all([
          getDocs(collection(db, "users")),
          getDoc(doc(db, "staffAttendance", dateKey)),
        ]);

        const users = usersSnapshot.docs.map((document) => {
          const data = document.data();

          return {
            id: document.id,
            fullName:
              data.fullName ||
              data.displayName ||
              data.name ||
              data.email ||
              "Unnamed User",
            email: data.email || "",
            profilePhoto:
              data.photoURL || data.photoUrl || data.profileImage || "",
            roles: normalizeRoleArray(data.roles ?? data.role ?? []),
            rollNo: getUserRollNo({ ...data, id: document.id }),
          };
        });

        const roleOptions = Array.from(
          new Set(users.flatMap((user) => user.roles)),
        ).sort((a, b) => a.localeCompare(b));

        const attendanceRecords = attendanceSnapshot.exists()
          ? attendanceSnapshot.data().records || {}
          : {};

        const presentByUser = {};
        users.forEach((user) => {
          const savedValue = attendanceRecords[user.id];
          presentByUser[user.id] =
            savedValue === true || savedValue === false ? savedValue : null;
        });

        return {
          date: dateKey,
          users,
          roleOptions,
          presentByUser,
          summary: buildSummary(users, presentByUser),
        };
      },
      STAFF_ATTENDANCE_CACHE_TTL_MS,
    );
  }

  async getTodayAttendanceData(options = {}) {
    return this.getAttendanceDataByDate(this.getTodayDateKey(), options);
  }

  async saveAttendance(dateKey, users, presentByUser) {
    const records = {};

    users.forEach((user) => {
      const value = presentByUser[user.id];
      if (value === true || value === false) {
        records[user.id] = value;
      }
    });

    await setDoc(doc(db, "staffAttendance", dateKey), {
      date: dateKey,
      records,
      updatedAt: serverTimestamp(),
    });

    invalidateCache(`staff:attendance:date:${dateKey}`);
  }
}

const staffAttendanceService = new StaffAttendanceService();

export default staffAttendanceService;
